import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { AuthContext } from '../App';

import ProgressBar from './ProgressBar';

function TenantApplicationView() {
  const { id } = useParams(); // Application ID
  const location = useLocation();
  const { user, authReady } = useContext(AuthContext);
  const [application, setApplication] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [files, setFiles] = useState({});
  const [uploadStatus, setUploadStatus] = useState({});
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tuReport, setTuReport] = useState(null);
  const [tuPurchasing, setTuPurchasing] = useState(false);
  const documentsSectionRef = useRef(null);
  const detailsSectionRef = useRef(null);
  const commentsSectionRef = useRef(null);
  const commentInputRef = useRef(null);

  const fetchDossier = useCallback(async () => {
    if (!authReady) return;
    if (!user || user.userType !== 'tenant') {
      setError('You must be logged in as a tenant to view this application.');
      setLoading(false);
      return;
    }
    const token = localStorage.getItem('token');
    try {
      const appRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/applications/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!appRes.ok) throw new Error('Could not fetch application details');
      const appData = await appRes.json();
      setApplication(appData.application);

      const [commentsRes, tuRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/applications/${id}/comments`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenant/applications/${id}/tu-report`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).catch(() => ({ ok: false }))
      ]);
      if (!commentsRes.ok) throw new Error('Could not fetch comments');
      const commentsData = await commentsRes.json();
      setComments((commentsData.comments || []).slice().sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
      if (tuRes.ok) {
        const tuData = await tuRes.json();
        setTuReport(tuData.report || null);
      }

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id, user, authReady]);

  useEffect(() => {
    fetchDossier();
  }, [fetchDossier]);

  const handlePostComment = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/applications/${id}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ comment_text: newComment, origin: 'application' })
      });
      if (!response.ok) throw new Error('Failed to post comment');
      const data = await response.json();
      setComments(prev => [...prev, data.comment]);
      setNewComment('');
    } catch (err) {
      setError(err.message);
    }
  };

  // Handle TU purchase return URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('tu_purchase') === 'success') {
      // Clean URL
      window.history.replaceState({}, '', location.pathname);
      // Re-fetch to pick up the completed report
      const poll = setInterval(async () => {
        const token = localStorage.getItem('token');
        try {
          const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenant/applications/${id}/tu-report`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (res.ok) {
            const data = await res.json();
            if (data.report?.status === 'completed') {
              setTuReport(data.report);
              clearInterval(poll);
            }
          }
        } catch {}
      }, 3000);
      // Stop polling after 60s
      setTimeout(() => clearInterval(poll), 60000);
      return () => clearInterval(poll);
    }
  }, [location.search, location.pathname, id]);

  const handlePurchaseTuReport = async () => {
    const token = localStorage.getItem('token');
    if (!token || !id) return;
    setTuPurchasing(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenant/applications/${id}/tu-report/purchase`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Unable to start checkout');
      if (data.url) window.location.href = data.url;
    } catch (err) {
      alert(err.message || 'Unable to purchase credit check');
    } finally {
      setTuPurchasing(false);
    }
  };

  const handleCancelApplication = async () => {
    const token = localStorage.getItem('token');
    if (!window.confirm('Are you sure you want to cancel this application?')) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/applications/${id}/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Failed to cancel application');
      setApplication(data.application || null);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleFileChange = (e) => {
    const { name, files: selectedFiles } = e.target;
    const file = selectedFiles && selectedFiles[0] ? selectedFiles[0] : null;
    setFiles(prev => ({ ...prev, [name]: file }));
    setError(null);
    setUploadStatus(prev => ({ ...prev, [name]: '' }));
  };

  const handleUpload = async (documentType) => {
    const file = files[documentType];
    if (!file) {
      setError(`Please select a file for ${documentType}.`);
      return;
    }

    const formData = new FormData();
    formData.append('documents', file);
    formData.append('document_type', documentType);

    try {
      const token = localStorage.getItem('token');
      setUploadStatus(prev => ({ ...prev, [documentType]: 'Uploading...' }));
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/applications/${id}/documents`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Upload failed');
      }

      setUploadStatus(prev => ({ ...prev, [documentType]: 'Uploaded ✔' }));
      fetchDossier(); // Refresh dossier to show new document
    } catch (err) {
      setUploadStatus(prev => ({ ...prev, [documentType]: 'Failed ✖' }));
      setError(err.message);
    }
  };

  const handleDownload = (docId) => {
    const token = localStorage.getItem('token');
    window.open(`${process.env.REACT_APP_API_BASE_URL}/api/documents/${docId}?token=${token}`, '_blank');
  };

  const documentTypes = [
    { id: 'government_id', label: 'Government ID' },
    { id: 'employment_letter', label: 'Letter of Employment' },
    { id: 'paystub', label: 'Most Recent Paystub' },
    { id: 'reference_letter', label: 'Reference Letter' },
    { id: 'bank_statement', label: 'Bank Statement (Optional)' },
  ];

  const getVerificationBadge = () => {
    if (!application) return { label: 'Unverified landlord', tone: 'warning' };
    if (application.landlord_ownership_status === 'approved' || application.landlord_id_status === 'verified') {
      return { label: 'Verified landlord', tone: 'paid' };
    }
    return { label: 'Unverified landlord', tone: 'warning' };
  };

  if (loading) return <main className="App-main">Loading application...</main>;
  if (error) return <main className="App-main"><p className="error-message">{error}</p></main>;
  if (!application) return <main className="App-main">Application not found.</main>;

  const statusLabels = {
    submitted: 'Submitted',
    in_review: 'In Review',
    docs_requested: 'Documents Requested',
    info_needed: 'In Review',
    approved: 'Approved',
    lease_tenant_signed: 'Tenant Signed',
    completed: 'Completed',
    rejected: 'Rejected'
  };

  const normalizedStatus = application.status === 'info_needed'
    ? 'in_review'
    : (application.status === 'lease_drafted_landlord_signed' ? 'approved' : application.status);

  const stages = [
    { key: 'submitted', label: statusLabels.submitted },
    { key: 'in_review', label: statusLabels.in_review },
    { key: 'docs_requested', label: statusLabels.docs_requested },
    { key: 'approved', label: statusLabels.approved },
    { key: 'lease_tenant_signed', label: statusLabels.lease_tenant_signed },
    { key: 'completed', label: statusLabels.completed },
    { key: 'rejected', label: statusLabels.rejected },
  ];
  const currentStage = stages.findIndex(stage => stage.key === normalizedStatus);

  return (
    <main className="App-main">
      <ProgressBar stages={stages} currentStage={currentStage} />
      <div className="status-control">
        <span>Status:</span>
        <span className={`status-${normalizedStatus}`}>{statusLabels[normalizedStatus] || normalizedStatus}</span>
        {application.status !== 'cancelled' && application.status !== 'rejected' && application.status !== 'completed' && (
          <button className="secondary-button" style={{ marginLeft: '1rem' }} onClick={handleCancelApplication}>
            Cancel Application
          </button>
        )}
      </div>
      <div className="ledger-header has-actions">
        <div>
          <p className="eyebrow-tag">Application</p>
          <h2>Application Status</h2>
          <p>Track screening status and upload required documents.</p>
        </div>
        <div className="ledger-actions">
          <Link to="/verification" className={`status-pill ${getVerificationBadge().tone}`} title="Learn what landlord verification means">
            {getVerificationBadge().label}
          </Link>
        </div>
      </div>

      {application.status === 'docs_requested' && (
        <div style={{
          marginTop: '0.75rem', padding: '0.75rem 1rem', background: '#FFF7ED',
          border: '1px solid #FCE7B2', borderRadius: '8px', borderLeft: '4px solid #D97706'
        }}>
          <p style={{ fontWeight: 600, color: '#92400E', margin: '0 0 0.25rem', fontSize: '0.9rem' }}>
            ⚠ The landlord has requested additional documents
          </p>
          <p style={{ margin: 0, color: '#92400E', fontSize: '0.82rem', opacity: 0.85 }}>
            Check the messages below for details on what is needed, then upload the requested documents.
          </p>
        </div>
      )}
      {location.state?.notice && (
        <p className="success-message" style={{ marginTop: '0.75rem' }}>
          {location.state.notice}
        </p>
      )}

      <div className="dossier-grid">
        <div className="dossier-section" ref={detailsSectionRef}>
          <h3>Application Details</h3>
          <p><strong>Property:</strong> {application.property_address}</p>
          <p><strong>City:</strong> {application.property_city}</p>
          <p><strong>Submitted on:</strong> {new Date(application.created_at).toLocaleDateString()}</p>
          <p><strong>Status:</strong> {statusLabels[application.status] || application.status}</p>
        </div>

        {/* Credit Check section */}
        <div className="dossier-section">
          <h3>Credit Check</h3>
          {tuReport?.status === 'completed' && tuReport?.score ? (
            <div style={{ background: '#F0FFF4', border: '1px solid #BBF7D0', borderRadius: '10px', padding: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '1.5rem' }}>✅</span>
                <div>
                  <p style={{ fontWeight: 600, color: '#16A34A', margin: '0 0 0.15rem', fontSize: '0.95rem' }}>Credit check completed</p>
                  {tuReport.score.score != null && (
                    <p style={{ margin: 0, fontSize: '0.88rem', color: '#0F2933' }}>
                      Score: <strong>{tuReport.score.score}</strong>
                      {tuReport.score.rating && ` (${tuReport.score.rating})`}
                      {tuReport.score.range && <span className="status-text"> out of {tuReport.score.range}</span>}
                    </p>
                  )}
                  <p className="status-text" style={{ margin: '0.15rem 0 0', fontSize: '0.78rem' }}>
                    Purchased {tuReport.purchased_at ? new Date(tuReport.purchased_at).toLocaleDateString('en-CA') : ''} by {tuReport.purchased_by === 'tenant' ? 'you' : 'landlord'}
                  </p>
                </div>
              </div>
            </div>
          ) : tuReport?.status === 'pending' ? (
            <div style={{ background: '#FFF7ED', border: '1px solid #FCE7B2', borderRadius: '10px', padding: '1rem', textAlign: 'center' }}>
              <p style={{ fontWeight: 500, color: '#92400E', margin: 0 }}>⏳ Credit check processing…</p>
              <p className="status-text" style={{ margin: '0.25rem 0 0' }}>Your report is being generated. This page will update automatically.</p>
            </div>
          ) : (
            <div style={{ border: '1px solid #E6EEF2', borderRadius: '10px', padding: '1.25rem', background: '#FAFCFF', textAlign: 'center' }}>
              <p style={{ fontWeight: 600, fontSize: '1rem', color: '#0F2933', margin: '0 0 0.35rem' }}>
                Strengthen your application with a credit check
              </p>
              <p style={{ fontSize: '0.85rem', color: '#667085', margin: '0 0 0.85rem', maxWidth: '420px', marginInline: 'auto' }}>
                A TransUnion credit check shows landlords you're financially reliable. Your score is shared with the landlord reviewing your application.
              </p>
              <button
                className="primary-button"
                type="button"
                onClick={handlePurchaseTuReport}
                disabled={tuPurchasing}
                style={{ minWidth: '220px' }}
              >
                {tuPurchasing ? 'Starting checkout…' : 'Get Credit Check - $29.99'}
              </button>
            </div>
          )}
        </div>

        <div className="dossier-section" ref={documentsSectionRef}>
          <h3>Your Documents</h3>
          {application.documents && application.documents.length > 0 ? (
            <ul className="document-list">
              {application.documents.map(doc => (
                <li key={doc.id}>
                  <button type="button" className="link-button" onClick={() => handleDownload(doc.id)}>
                    {doc.original_filename}
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p>You have not uploaded any documents yet.</p>
          )}

          <hr style={{margin: '2rem 0'}} />
          <h4>Upload Additional Documents</h4>
          {documentTypes.map(({ id, label }) => (
            <div key={id} className="form-group document-upload-item">
              <label htmlFor={id}>{label}:</label>
              <div className="upload-controls">
                <label className="file-button" htmlFor={id}>
                  Choose File
                  <input type="file" id={id} name={id} onChange={handleFileChange} />
                </label>
                <span className="file-name">{files[id]?.name || 'No file chosen'}</span>
                <button className="secondary-button upload-button" onClick={() => handleUpload(id)} disabled={!files[id] || uploadStatus[id]?.includes('Uploaded')}>Upload</button>
              </div>
              {uploadStatus[id] && <p className={`upload-status ${uploadStatus[id].toLowerCase()}`}>{uploadStatus[id]}</p>}
            </div>
          ))}
        </div>

        <div className="dossier-section comments-section" ref={commentsSectionRef}>
          <h3>Communication with Landlord</h3>
           <div className="comment-list">
            {comments.map(c => {
              const docRequestMatch = c.comment_text?.match(/^\[Document Request - (.+?)\]\s*(.*)$/);
              if (docRequestMatch) {
                return (
                  <div key={c.id} className={`comment ${c.user_type}`} style={{
                    background: '#FFF7ED', border: '1px solid #FCE7B2', borderRadius: '8px', padding: '0.65rem 0.85rem'
                  }}>
                    <p style={{ fontWeight: 600, fontSize: '0.85rem', color: '#92400E', margin: '0 0 0.25rem' }}>
                      📩 Document requested: {docRequestMatch[1]}
                    </p>
                    <p style={{ margin: 0, color: '#0F2933' }}>{docRequestMatch[2]}</p>
                    <small>By landlord on {new Date(c.created_at).toLocaleDateString('en-CA') + ' ' + new Date(c.created_at).toLocaleTimeString('en-CA', { hour: '2-digit', minute: '2-digit' })}</small>
                  </div>
                );
              }
              return (
                <div key={c.id} className={`comment ${c.user_type}`}>
                  <p>{c.comment_text}</p>
                  <small>By {c.user_type} on {new Date(c.created_at).toLocaleDateString('en-CA') + ' ' + new Date(c.created_at).toLocaleTimeString('en-CA', { hour: '2-digit', minute: '2-digit' })}</small>
                </div>
              );
            })}
          </div>
          <div className="comment-form">
            <textarea ref={commentInputRef} value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder="Type a message..." />
            <button onClick={handlePostComment} className="primary-button">Send</button>
          </div>
        </div>
      </div>
    </main>
  );
}

export default TenantApplicationView;
